import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { conversationService } from '../../features/conversations/services/conversationService';
import { buildConversationShortcutGroups } from '../utils/conversationShortcuts';
import styles from './ConversationShortcutMenu.module.css';

interface ConversationShortcutMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelectCommand: (command: string) => void;
  buttonClassName: string;
  iconClassName: string;
  disabled?: boolean;
  title?: string;
}

const ConversationShortcutMenu: React.FC<ConversationShortcutMenuProps> = ({
  isOpen,
  onToggle,
  onClose,
  onSelectCommand,
  buttonClassName,
  iconClassName,
  disabled = false,
  title,
}) => {
  const { t } = useTranslation(['conversations', 'common']);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [templateGroups, setTemplateGroups] = useState(() => buildConversationShortcutGroups([]));
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);
  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [createTemplateError, setCreateTemplateError] = useState<string | null>(null);
  const [createTemplateSuccess, setCreateTemplateSuccess] = useState<string | null>(null);

  const shortcutGroups = useMemo(() => templateGroups, [templateGroups]);

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    setTemplateLoadError(null);

    try {
      const templates = await conversationService.getMessageTemplates();
      setTemplateGroups(buildConversationShortcutGroups(templates));
    } catch {
      setTemplateLoadError(t('failedToLoadTemplates', { ns: 'conversations' }));
      setTemplateGroups(buildConversationShortcutGroups([]));
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [t]);

  const resetCreateTemplateForm = useCallback(() => {
    setNewTemplateName('');
    setNewTemplateContent('');
    setCreateTemplateError(null);
    setCreateTemplateSuccess(null);
    setIsCreatingTemplate(false);
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!isOpen) {
      setIsCreateTemplateOpen(false);
      resetCreateTemplateForm();
    }
  }, [isOpen, resetCreateTemplateForm]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updateMenuPosition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const viewportPadding = 8;
      const gap = 8;

      const maxLeft = Math.max(viewportPadding, window.innerWidth - menuRect.width - viewportPadding);
      const left = Math.min(Math.max(triggerRect.left, viewportPadding), maxLeft);

      // Prefer above the trigger but fall back below when there is not enough room.
      let top = triggerRect.top - menuRect.height - gap;
      if (top < viewportPadding) {
        top = triggerRect.bottom + gap;
      }

      const maxTop = Math.max(viewportPadding, window.innerHeight - menuRect.height - viewportPadding);
      top = Math.min(Math.max(top, viewportPadding), maxTop);

      setMenuPosition({ top, left });
    };

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInTrigger = rootRef.current?.contains(target);
      const isInMenu = menuRef.current?.contains(target);
      if (!isInTrigger && !isInMenu) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const onCreateTemplateSubmit = useCallback(async () => {
    const trimmedName = newTemplateName.trim();
    const trimmedContent = newTemplateContent.trim();

    if (!trimmedName) {
      setCreateTemplateError(t('templateNameRequired', { ns: 'conversations' }));
      return;
    }

    if (!trimmedContent) {
      setCreateTemplateError(t('templateContentRequired', { ns: 'conversations' }));
      return;
    }

    setIsCreatingTemplate(true);
    setCreateTemplateError(null);
    setCreateTemplateSuccess(null);

    try {
      await conversationService.createMessageTemplate(trimmedName, trimmedContent);
      await loadTemplates();

      setCreateTemplateSuccess(t('templateCreated', { ns: 'conversations' }));
      setNewTemplateName('');
      setNewTemplateContent('');
      setIsCreateTemplateOpen(false);
    } catch (error) {
      setCreateTemplateError(error instanceof Error ? error.message : t('failedToCreateTemplate', { ns: 'conversations' }));
    } finally {
      setIsCreatingTemplate(false);
    }
  }, [loadTemplates, newTemplateContent, newTemplateName, t]);

  const onShortcutItemClick = useCallback((itemId: string, insertText: string) => {
    if (itemId === 'create-template') {
      setIsCreateTemplateOpen(current => !current);
      setCreateTemplateError(null);
      setCreateTemplateSuccess(null);
      return;
    }

    onSelectCommand(insertText);
    onClose();
  }, [onClose, onSelectCommand]);

  return (
    <div className={styles.wrapper} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={buttonClassName}
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={title ?? t('runShortcut', { ns: 'conversations' })}
      >
        <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M13 4 6 14h5l-1 6 8-11h-5l1-5Z" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          role="menu"
          aria-label={t('conversationShortcuts', { ns: 'conversations' })}
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          {createTemplateSuccess && (
            <div className={styles.successText}>{createTemplateSuccess}</div>
          )}

          {isLoadingTemplates && (
            <div className={styles.statusText}>{t('loadingTemplates', { ns: 'conversations' })}</div>
          )}

          {templateLoadError && (
            <div className={styles.statusText}>{templateLoadError}</div>
          )}

          {shortcutGroups.map(group => (
            <div key={group.id} className={styles.group}>
              <div className={styles.groupLabel}>{group.label}</div>
              {group.items.map(item => (
                <React.Fragment key={item.id}>
                  <button
                    type="button"
                    className={styles.item}
                    role="menuitem"
                    onClick={() => onShortcutItemClick(item.id, item.insertText)}
                  >
                    <span className={styles.itemLabel}>{item.label}</span>
                    <span className={styles.itemDescription}>{item.description}</span>
                    {item.commandHint && (
                      <span className={styles.itemCommand}>{item.commandHint}</span>
                    )}
                  </button>

                  {item.id === 'create-template' && isCreateTemplateOpen && (
                    <div className={styles.createTemplatePanel}>
                      <label className={styles.fieldLabel} htmlFor="shortcut-template-name">{t('templateName', { ns: 'conversations' })}</label>
                      <input
                        id="shortcut-template-name"
                        className={styles.fieldInput}
                        value={newTemplateName}
                        onChange={(event) => setNewTemplateName(event.target.value)}
                        placeholder={t('templateNamePlaceholder', { ns: 'conversations' })}
                        maxLength={120}
                        disabled={isCreatingTemplate}
                      />

                      <label className={styles.fieldLabel} htmlFor="shortcut-template-content">{t('templateContent', { ns: 'conversations' })}</label>
                      <textarea
                        id="shortcut-template-content"
                        className={styles.fieldTextarea}
                        value={newTemplateContent}
                        onChange={(event) => setNewTemplateContent(event.target.value)}
                        placeholder={t('templateContentPlaceholder', { ns: 'conversations' })}
                        maxLength={8000}
                        rows={4}
                        disabled={isCreatingTemplate}
                      />

                      {createTemplateError && (
                        <div className={styles.errorText}>{createTemplateError}</div>
                      )}

                      <div className={styles.createTemplateActions}>
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => {
                            setIsCreateTemplateOpen(false);
                            resetCreateTemplateForm();
                          }}
                          disabled={isCreatingTemplate}
                        >
                          {t('cancel', { ns: 'common' })}
                        </button>
                        <button
                          type="button"
                          className={styles.primaryBtn}
                          onClick={() => void onCreateTemplateSubmit()}
                          disabled={isCreatingTemplate}
                        >
                          {isCreatingTemplate ? t('saving', { ns: 'common' }) : t('saveTemplate', { ns: 'conversations' })}
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export { ConversationShortcutMenu };
export default ConversationShortcutMenu;